/**
 * PnLBalanceSheetFlow — Door 2 of /switch-from-quickbooks.
 *
 * The "match my QuickBooks reports" path. User uploads two CSVs they
 * already know how to find: P&L and Balance Sheet. Backend parses
 * both, classifies each row into asset/liab/equity/revenue/expense,
 * runs AI account mapping, and posts ONE balanced opening JE on
 * cutover_date. Net income from the P&L auto-plugs into Retained
 * Earnings so the JE always balances.
 *
 * UX priority: explicit step-by-step instructions with the exact
 * menu path AND default filename for each export, so a non-accountant
 * user can complete the flow without ever asking for help.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Calendar, Upload, Check, Loader2,
  FileText, FileSpreadsheet, BarChart3, Sparkles, AlertCircle,
  CheckCircle2, ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function lastDayOfPriorMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10);
}

function fmt(n) {
  const v = Number(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

const STEPS = [
  { id: 'date',    label: 'Pick a date',     icon: Calendar },
  { id: 'pnl',     label: 'P&L',             icon: BarChart3 },
  { id: 'bs',      label: 'Balance Sheet',   icon: FileText },
  { id: 'review',  label: 'Review & post',   icon: Check },
  { id: 'done',    label: 'Done',            icon: CheckCircle2 },
];

export default function PnLBalanceSheetFlow({ workspaceId, onBack }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [cutoverDate, setCutoverDate] = useState(lastDayOfPriorMonth());
  const pnlFileRef = useRef(null);
  const bsFileRef = useRef(null);
  const [pnlFile, setPnlFile] = useState(null);
  const [bsFile, setBsFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({}); // { qb_account: account_id }
  const [cbAccounts, setCbAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  // Load CountBucks accounts for the dropdowns.
  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/accounts`);
        if (r.ok) setCbAccounts(await r.json());
      } catch { /* ignore */ }
    })();
  }, [workspaceId]);

  const accountOptionsBySection = useMemo(() => {
    const byType = { asset: [], liability: [], equity: [], revenue: [], expense: [] };
    (cbAccounts || []).forEach((a) => {
      const t = (a.type || '').toLowerCase();
      if (byType[t]) byType[t].push(a);
    });
    return byType;
  }, [cbAccounts]);

  const previewRows = useMemo(() => {
    if (!preview) return [];
    return [...(preview.pnl_preview || []), ...(preview.bs_preview || [])];
  }, [preview]);

  const fetchPreview = async () => {
    if (!pnlFile || !bsFile) {
      toast.error('Pick both CSVs first');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('pnl_file', pnlFile);
      fd.append('bs_file', bsFile);
      fd.append('workspace_id', workspaceId);
      fd.append('cutover_date', cutoverDate);
      const r = await apiFetch(`${API_URL}/api/migration/import/pnl-bs`, {
        method: 'POST', body: fd,
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Could not parse the files');
        return;
      }
      setPreview(d);
      // Seed mapping with fuzzy suggestions.
      const seed = {};
      [...(d.pnl_preview || []), ...(d.bs_preview || [])].forEach((row) => {
        if (row.suggested_account_id) seed[row.qb_account] = row.suggested_account_id;
      });
      setMapping(seed);
      setStepIdx(3);
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setBusy(false);
  };

  const commit = async () => {
    if (!preview) return;
    const rows = previewRows
      .map((r) => ({
        section: r.section,
        amount: r.amount,
        account_id: mapping[r.qb_account],
        qb_account: r.qb_account,
      }))
      .filter((r) => r.account_id && r.amount !== 0);

    const unmapped = previewRows.length - rows.length;
    if (rows.length === 0) {
      toast.error('No rows are mapped to an account yet.');
      return;
    }
    if (unmapped > 0) {
      const ok = window.confirm(
        `${unmapped} row(s) are unmapped and will be skipped. The opening JE may not perfectly match your QB reports. Continue?`
      );
      if (!ok) return;
    }

    setCommitting(true);
    try {
      const r = await apiFetch(`${API_URL}/api/migration/import/pnl-bs/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          cutover_date: cutoverDate,
          rows,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Could not post opening JE');
        return;
      }
      setResult(d);
      setStepIdx(4);
      toast.success(`Opening JE posted · ${d.lines_posted} lines · ${fmt(d.total_debit)} Dr / ${fmt(d.total_credit)} Cr`);
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setCommitting(false);
  };

  const renderStepper = () => (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const done = i < stepIdx;
        const active = i === stepIdx;
        return (
          <div
            key={s.id}
            data-testid={`pnlbs-step-${s.id}`}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border',
              done && 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
              active && 'bg-emerald-600 text-white border-emerald-600 font-semibold',
              !done && !active && 'bg-muted text-muted-foreground border-transparent',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {s.label}
          </div>
        );
      })}
    </div>
  );

  // STEP 4 — Done
  if (stepIdx === 4 && result) {
    return (
      <Card data-testid="pnlbs-done">
        <CardContent className="p-8 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <div>
            <h3 className="text-2xl font-bold">Your books now match QuickBooks 🎉</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Posted JE #{result.entry_no} on <span className="font-mono font-semibold text-foreground">{cutoverDate}</span> ·
              {' '}{result.lines_posted} lines · {fmt(result.total_debit)} Dr / {fmt(result.total_credit)} Cr
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto pt-2 text-left">
            <Link
              to="/banking"
              className="rounded-xl border bg-card p-4 hover:border-emerald-500/40 hover:shadow-sm transition-all"
              data-testid="pnlbs-next-banking"
            >
              <p className="font-semibold text-sm">Connect your bank →</p>
              <p className="text-xs text-muted-foreground mt-1">
                Bring in everything since {cutoverDate} automatically.
              </p>
            </Link>
            <Link
              to="/reports/balance-sheet"
              className="rounded-xl border bg-card p-4 hover:border-emerald-500/40 hover:shadow-sm transition-all"
              data-testid="pnlbs-next-reports"
            >
              <p className="font-semibold text-sm">Verify your reports →</p>
              <p className="text-xs text-muted-foreground mt-1">
                Open the Balance Sheet and confirm it matches QuickBooks.
              </p>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="pnlbs-flow">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              Match my QuickBooks reports
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Upload your P&amp;L and Balance Sheet. We'll create your opening balances.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="pnlbs-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
        <div className="mt-4">{renderStepper()}</div>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* STEP 0 — Date */}
        {stepIdx === 0 && (
          <div className="space-y-4" data-testid="pnlbs-step-date-panel">
            <Label className="text-sm font-medium">Pick your cutover date</Label>
            <Input
              type="date"
              value={cutoverDate}
              onChange={(e) => setCutoverDate(e.target.value)}
              className="max-w-xs"
              data-testid="pnlbs-date-input"
            />
            <p className="text-xs text-muted-foreground max-w-xl">
              This is the "as of" date on your Balance Sheet AND the end date of your P&amp;L.
              Best practice: last day of last month so the month boundary is clean.
            </p>
            <Button
              onClick={() => setStepIdx(1)}
              disabled={!cutoverDate}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="pnlbs-date-next"
            >
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* STEP 1 — P&L upload */}
        {stepIdx === 1 && (
          <div className="space-y-4" data-testid="pnlbs-step-pnl-panel">
            <Instructions
              title="Step 1 of 3 — Export your Profit &amp; Loss"
              steps={[
                <>In QuickBooks, click <Code>Reports</Code> in the left sidebar.</>,
                <>Click <Code>Profit and Loss</Code> under the <strong>Business overview</strong> section.</>,
                <>Set <strong>Report period</strong> to <Code>All Dates</Code>, then set the <strong>To</strong> date to <Code>{cutoverDate}</Code>.</>,
                <>Top right → click <Code>Export</Code> → <Code>Export to Excel</Code> (or CSV).</>,
                <>If you got an .xlsx file, open it and re-save as <Code>.csv</Code>. Default filename will be something like <Code>Profit_and_Loss.csv</Code>.</>,
                <>Drop the file below ↓</>,
              ]}
            />
            <FileDrop
              testId="pnlbs-pnl-drop"
              file={pnlFile}
              accept=".csv,.CSV"
              onChange={(f) => setPnlFile(f)}
              hint="Drop your Profit_and_Loss.csv here"
            />
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStepIdx(0)} data-testid="pnlbs-pnl-prev">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                onClick={() => setStepIdx(2)}
                disabled={!pnlFile}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="pnlbs-pnl-next"
              >
                Continue to Balance Sheet <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — BS upload */}
        {stepIdx === 2 && (
          <div className="space-y-4" data-testid="pnlbs-step-bs-panel">
            <Instructions
              title="Step 2 of 3 — Export your Balance Sheet"
              steps={[
                <>In QuickBooks, click <Code>Reports</Code> in the left sidebar.</>,
                <>Click <Code>Balance Sheet</Code> under the <strong>Business overview</strong> section.</>,
                <>Set the <strong>As of</strong> date to <Code>{cutoverDate}</Code>.</>,
                <>Top right → <Code>Export</Code> → <Code>Export to Excel</Code> (or CSV).</>,
                <>If you got .xlsx, re-save as <Code>.csv</Code>. Default filename: <Code>Balance_Sheet.csv</Code>.</>,
                <>Drop the file below ↓</>,
              ]}
            />
            <FileDrop
              testId="pnlbs-bs-drop"
              file={bsFile}
              accept=".csv,.CSV"
              onChange={(f) => setBsFile(f)}
              hint="Drop your Balance_Sheet.csv here"
            />
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStepIdx(1)} data-testid="pnlbs-bs-prev">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                onClick={fetchPreview}
                disabled={!bsFile || busy}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="pnlbs-bs-parse"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Parse both files
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Review */}
        {stepIdx === 3 && preview && (
          <div className="space-y-4" data-testid="pnlbs-step-review-panel">
            <Instructions
              title="Step 3 of 3 — Review &amp; post"
              steps={[
                <>We extracted <strong>{previewRows.length}</strong> account rows from your reports.</>,
                <>Each row is auto-matched to a CountBucks account based on name similarity. Adjust any wrong matches in the dropdowns below.</>,
                <>Only <strong>Balance Sheet</strong> rows post as journal entry lines. P&amp;L rows are shown for validation — your BS Equity already reflects YTD net income.</>,
                <>Click <strong>Post opening JE</strong> when ready. Nothing is final until you click that button.</>,
              ]}
            />

            {/* Totals summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Assets" value={fmt(preview.totals.bs_assets)} tone="positive" />
              <Stat label="Liabilities + Equity" value={fmt(preview.totals.bs_liabilities + preview.totals.bs_equity)} />
              <Stat label="Revenue (YTD)" value={fmt(preview.totals.pnl_revenue)} tone="positive" />
              <Stat label="Net income" value={fmt(preview.totals.net_income)} tone={preview.totals.net_income >= 0 ? 'positive' : 'negative'} />
            </div>

            {Math.abs(preview.totals.balance_delta || 0) > 1 && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <strong>Heads up — off by {fmt(Math.abs(preview.totals.balance_delta))}.</strong>{' '}
                  We'll auto-plug the difference into Retained Earnings so the JE balances. This is normal for
                  QB exports that include "Net Income" as a separate line.
                </div>
              </div>
            )}

            {/* Row mapping table */}
            <div className="rounded-lg border max-h-[400px] overflow-auto" data-testid="pnlbs-review-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs">
                  <tr className="text-left">
                    <th className="px-2 py-2">QB account</th>
                    <th className="px-2 py-2">Section</th>
                    <th className="px-2 py-2 text-right">Amount</th>
                    <th className="px-2 py-2">→ CountBucks account</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r, i) => {
                    const opts = accountOptionsBySection[r.section] || [];
                    return (
                      <tr key={`${r.source}-${i}`} className="border-t hover:bg-muted/20">
                        <td className="px-2 py-1.5 font-medium">{r.qb_account}</td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className="text-[10px] capitalize">{r.section}</Badge>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmt(r.amount)}</td>
                        <td className="px-2 py-1.5">
                          <select
                            value={mapping[r.qb_account] || ''}
                            onChange={(e) => setMapping({ ...mapping, [r.qb_account]: e.target.value })}
                            className="text-xs bg-background border rounded px-1.5 py-1 w-full max-w-[260px]"
                            data-testid={`pnlbs-map-${i}`}
                          >
                            <option value="">— unmapped — skip this row —</option>
                            {opts.map((a) => (
                              <option key={a.account_id} value={a.account_id}>
                                {a.code ? `${a.code} · ` : ''}{a.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStepIdx(2)} data-testid="pnlbs-review-prev">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                onClick={commit}
                disabled={committing}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="pnlbs-commit"
              >
                {committing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Post opening JE
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function Code({ children }) {
  return (
    <code className="font-mono text-[12px] bg-muted px-1.5 py-0.5 rounded border">{children}</code>
  );
}

function Instructions({ title, steps }) {
  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-2.5">
      <p className="text-sm font-semibold">{title}</p>
      <ol className="space-y-1.5 text-sm text-muted-foreground">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-emerald-500 font-bold tabular-nums w-5 shrink-0">{i + 1}.</span>
            <span className="leading-relaxed">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function FileDrop({ testId, file, accept, onChange, hint }) {
  return (
    <label
      className={cn(
        'flex flex-col items-center justify-center border-2 border-dashed rounded-xl py-8 cursor-pointer transition-colors',
        file
          ? 'border-emerald-500/40 bg-emerald-500/5'
          : 'border-border hover:border-emerald-500/40 hover:bg-muted/30'
      )}
      data-testid={testId}
    >
      <input
        type="file"
        accept={accept}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="hidden"
      />
      {file ? (
        <div className="text-center">
          <CheckCircle2 className="w-7 h-7 text-emerald-500 mx-auto mb-1.5" />
          <p className="text-sm font-medium">{file.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{(file.size / 1024).toFixed(1)} KB · click to swap</p>
        </div>
      ) : (
        <div className="text-center">
          <Upload className="w-7 h-7 text-muted-foreground mx-auto mb-1.5" />
          <p className="text-sm font-medium">{hint}</p>
          <p className="text-xs text-muted-foreground mt-0.5">CSV only · or click to browse</p>
        </div>
      )}
    </label>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn(
        'text-lg font-semibold tabular-nums mt-0.5',
        tone === 'positive' && 'text-emerald-600 dark:text-emerald-400',
        tone === 'negative' && 'text-rose-600 dark:text-rose-400',
      )}>
        {value}
      </p>
    </div>
  );
}
